import React, { useState, useEffect, useCallback } from "react";
import Axios from "axios";
import { useSelector } from "react-redux";
import { confirmAlert } from "react-confirm-alert";
import AkkhorLayout from "../layout/akkhor";
import SearchBoxModal from "../component/SearchBoxModal";
import { URL_LIST_ALL } from "../configs/api";

// Trang CÂY NHÂN SỰ — dựng từ hồ sơ nhân sự.
//  - mode "manager": theo quản lý trực tiếp (users.master_id), gốc = Chủ tịch.
//  - mode "org": Công ty > Phòng ban (đa cấp) > Người.
// Backend: GET /api/caynhansu?mode=manager|org[&company_id=]

// ---- Node 1 người trong cây quản lý ----
function PersonNode({ node, depth, search, onSelect }) {
  const hasChildren = node.children && node.children.length > 0;
  // mặc định mở 2 cấp đầu, sâu hơn thì thu lại cho gọn
  const [open, setOpen] = useState(depth < 2);

  // đếm tổng cấp dưới (đệ quy) để hiện badge
  const countAll = useCallback((n) => {
    let t = n.children ? n.children.length : 0;
    if (n.children) n.children.forEach((c) => (t += countAll(c)));
    return t;
  }, []);

  const matched =
    search &&
    (String(node.fullname || "").toLowerCase().includes(search) ||
      String(node.code || "").toLowerCase().includes(search) ||
      String(node.chucvu || "").toLowerCase().includes(search));

  return (
    <li>
      <div
        className="d-flex align-items-center py-1"
        style={{
          background: matched ? "#fff3cd" : "transparent",
          borderRadius: 4,
        }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="btn btn-sm p-0 me-1"
            style={{ width: 20, color: "#666" }}
            title={open ? "Thu gọn" : "Mở rộng"}
          >
            <span className={open ? "fas fa-minus-square" : "fas fa-plus-square"} />
          </button>
        ) : (
          <span style={{ display: "inline-block", width: 20 }} />
        )}
        <span
          className="cursor-pointer"
          style={{ cursor: "pointer" }}
          onClick={() => onSelect && onSelect(node)}
        >
          <strong>{node.fullname}</strong>
          <small className="text-muted"> — {node.chucvu || "Chưa có chức vụ"}</small>
          {node.part && <small className="text-muted"> · {node.part}</small>}
          {hasChildren && (
            <span className="badge bg-info ms-2">{countAll(node)}</span>
          )}
        </span>
      </div>
      {hasChildren && open && (
        <ul style={{ listStyle: "none", paddingLeft: 22, borderLeft: "1px dashed #ccc", marginLeft: 8 }}>
          {node.children.map((c) => (
            <PersonNode key={c.id} node={c} depth={depth + 1} search={search} onSelect={onSelect} />
          ))}
        </ul>
      )}
    </li>
  );
}

// ---- Node phòng ban (mode org) ----
function PartNode({ part, depth, onSelect }) {
  const [open, setOpen] = useState(depth < 1);
  const hasSub = part.sub_parts && part.sub_parts.length > 0;
  const hasMembers = part.members && part.members.length > 0;
  return (
    <li>
      <div className="py-1">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="btn btn-sm p-0 me-1"
          style={{ width: 20, color: "#666" }}
        >
          <span className={open ? "fas fa-minus-square" : "fas fa-plus-square"} />
        </button>
        <span className="fas fa-sitemap text-primary me-1" />
        <strong>{part.title}</strong>
        <span className="badge bg-secondary ms-2">{part.count}</span>
      </div>
      {open && (
        <ul style={{ listStyle: "none", paddingLeft: 22, borderLeft: "1px dashed #ccc", marginLeft: 8 }}>
          {hasSub &&
            part.sub_parts.map((sp) => (
              <PartNode key={"p" + sp.id} part={sp} depth={depth + 1} onSelect={onSelect} />
            ))}
          {hasMembers &&
            part.members.map((m) => (
              <li key={"u" + m.id} className="py-1">
                <span style={{ display: "inline-block", width: 20 }} />
                <span className="fas fa-user text-muted me-1" />
                <span style={{ cursor: "pointer" }} onClick={() => onSelect && onSelect(m)}>
                  {m.fullname}
                  <small className="text-muted"> — {m.chucvu || "—"}</small>
                </span>
              </li>
            ))}
        </ul>
      )}
    </li>
  );
}

export default function CayNhanSu() {
  const { user } = useSelector((state) => state.auth);
  const [mode, setMode] = useState("manager");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [err, setErr] = useState("");
  const [reload, setReload] = useState(0);          // bump để tải lại cây sau khi đổi quản lý
  const [editMaster, setEditMaster] = useState(null); // {id, fullname, master_id} khi mở modal sửa quản lý
  const [newMasterId, setNewMasterId] = useState(""); // master_id mới chọn trong modal
  const [savingMaster, setSavingMaster] = useState(false);

  useEffect(() => {
    setLoading(true);
    setErr("");
    setData(null);      // reset dữ liệu cũ: tránh render layout mode mới bằng data mode cũ (gây crash khi đổi tab)
    setSelected(null);  // đóng panel chi tiết của người thuộc mode cũ
    Axios.get(URL_LIST_ALL + "caynhansu?mode=" + mode, user.config)
      .then((res) => {
        if (res.data && res.data.status === "success") setData(res.data);
        else setErr((res.data && res.data.message) || "Không tải được cây nhân sự");
      })
      .catch((e) => setErr("Lỗi kết nối: " + e.message))
      .finally(() => setLoading(false));
  }, [mode, reload, user.config]);

  // Mở modal sửa người quản lý trực tiếp (quanly_id, fallback master_id nếu chưa có quanly_id)
  const openEditMaster = (person) => {
    setEditMaster(person);
    setNewMasterId(person.manager_id || "");
  };

  // Lưu người quản lý trực tiếp mới (PUT /api/users/{id} { quanly_id }).
  const saveMaster = () => {
    if (!editMaster) return;
    if (String(newMasterId) === String(editMaster.id)) {
      confirmAlert({ title: "Lỗi", message: "Không thể chọn chính người này làm quản lý.", buttons: [{ label: "OK" }] });
      return;
    }
    setSavingMaster(true);
    Axios.put(
      URL_LIST_ALL + "users/" + editMaster.id,
      { quanly_id: newMasterId || null },
      user.config
    )
      .then((res) => {
        setSavingMaster(false);
        if (res.data && res.data.status === "success") {
          setEditMaster(null);
          setSelected(null);
          confirmAlert({ title: "Thành công", message: "Đã cập nhật quản lý trực tiếp.", buttons: [{ label: "OK" }] });
          setReload((v) => v + 1); // tải lại cây để phản ánh cấu trúc mới
        } else {
          confirmAlert({ title: "Lỗi", message: (res.data && res.data.message) || "Cập nhật thất bại", buttons: [{ label: "OK" }] });
        }
      })
      .catch((e) => {
        setSavingMaster(false);
        confirmAlert({ title: "Lỗi", message: "Lỗi kết nối: " + e.message, buttons: [{ label: "OK" }] });
      });
  };

  const searchLower = search.trim().toLowerCase();

  return (
    <AkkhorLayout idrole="cay-nhan-su">
      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <h5 className="mb-0 me-3">
              <span className="fas fa-sitemap me-2" />Cây nhân sự
            </h5>
            <div className="btn-group" role="group">
              <button
                type="button"
                className={"btn btn-sm " + (mode === "manager" ? "btn-primary" : "btn-outline-primary")}
                onClick={() => setMode("manager")}
              >
                Theo quản lý
              </button>
              <button
                type="button"
                className={"btn btn-sm " + (mode === "org" ? "btn-primary" : "btn-outline-primary")}
                onClick={() => setMode("org")}
              >
                Theo công ty / phòng ban
              </button>
            </div>
            <div className="ms-auto" style={{ minWidth: 240 }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Tìm theo tên / mã / chức vụ..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {data && (
            <div className="text-muted mb-2">
              <small>
                Tổng: <strong>{data.total}</strong> nhân sự đang đi làm
                {mode === "manager" && data.orphan_masters > 0 && (
                  <span className="text-warning">
                    {" "}· {data.orphan_masters} người có quản lý đã nghỉ (hiện thành gốc riêng)
                  </span>
                )}
              </small>
            </div>
          )}

          {loading && <div className="text-center p-4"><span className="fas fa-spinner fa-spin" /> Đang tải...</div>}
          {err && <div className="alert alert-danger">{err}</div>}

          <div className="row">
            <div className={selected ? "col-lg-8" : "col-12"}>
              {data && !loading && data.mode === mode && Array.isArray(data.roots) && (
                <ul style={{ listStyle: "none", paddingLeft: 0 }}>
                  {mode === "manager" &&
                    data.roots.map((r) => (
                      <PersonNode key={r.id} node={r} depth={0} search={searchLower} onSelect={setSelected} />
                    ))}
                  {mode === "org" &&
                    data.roots.map((c) => (
                      <li key={"c" + c.id}>
                        <div className="py-1">
                          <span className="fas fa-building text-success me-1" />
                          <strong style={{ fontSize: "1.05em" }}>{c.title}</strong>
                          <span className="badge bg-success ms-2">{c.count}</span>
                        </div>
                        <ul style={{ listStyle: "none", paddingLeft: 22 }}>
                          {(c.parts || []).map((p) => (
                            <PartNode key={"p" + p.id} part={p} depth={0} onSelect={setSelected} />
                          ))}
                          {c.members_no_part &&
                            c.members_no_part.map((m) => (
                              <li key={"u" + m.id} className="py-1">
                                <span className="fas fa-user text-muted me-1" />
                                <span style={{ cursor: "pointer" }} onClick={() => setSelected(m)}>
                                  {m.fullname} <small className="text-muted">— {m.chucvu || "—"}</small>
                                </span>
                              </li>
                            ))}
                        </ul>
                      </li>
                    ))}
                </ul>
              )}
            </div>

            {selected && (
              <div className="col-lg-4">
                <div className="card border">
                  <div className="card-body">
                    <button
                      type="button"
                      className="btn-close float-end"
                      onClick={() => setSelected(null)}
                    />
                    <h6>{selected.fullname}</h6>
                    <p className="mb-1"><small className="text-muted">Mã:</small> {selected.code}</p>
                    <p className="mb-1"><small className="text-muted">Chức vụ:</small> {selected.chucvu || "—"}</p>
                    <p className="mb-1"><small className="text-muted">Phòng ban:</small> {selected.part || "—"}</p>
                    <p className="mb-1"><small className="text-muted">Công ty:</small> {selected.company || "—"}</p>
                    {selected.email && <p className="mb-1"><small className="text-muted">Email:</small> {selected.email}</p>}
                    <p className="mb-1">
                      <small className="text-muted">Quản lý trực tiếp:</small> {selected.master_name || "— (chưa gán)"}
                      <span
                        className="fas fa-pencil-alt text-primary ms-2"
                        title="Sửa người quản lý trực tiếp"
                        style={{ cursor: "pointer" }}
                        onClick={() => openEditMaster(selected)}
                      />
                    </p>
                    {selected.children && (
                      <p className="mb-1"><small className="text-muted">Cấp dưới trực tiếp:</small> {selected.children.length}</p>
                    )}
                    <a href={"/edit-users/" + selected.id} className="btn btn-sm btn-outline-primary mt-2">
                      Xem hồ sơ
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal sửa người quản lý trực tiếp */}
      {editMaster && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.4)" }}
          onClick={(e) => { if (e.target === e.currentTarget && !savingMaster) setEditMaster(null); }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Sửa quản lý trực tiếp — {editMaster.fullname}</h6>
                <button type="button" className="btn-close" onClick={() => !savingMaster && setEditMaster(null)} />
              </div>
              <div className="modal-body">
                <label className="mb-1"><small className="text-muted">Chọn người quản lý trực tiếp mới:</small></label>
                <SearchBoxModal
                  apiname={"users"}
                  placeholder="Chọn nhân sự"
                  colvalue={"id"}
                  colname={"fullname"}
                  defaultValue={newMasterId}
                  name="new_master_id"
                  onChange={(e) => setNewMasterId(e)}
                />
                <small className="text-muted d-block mt-2">
                  Để trống nếu người này là cấp cao nhất (không có quản lý).
                </small>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-sm btn-secondary" disabled={savingMaster} onClick={() => setEditMaster(null)}>
                  Hủy
                </button>
                <button type="button" className="btn btn-sm btn-primary" disabled={savingMaster} onClick={saveMaster}>
                  {savingMaster ? <span className="fas fa-spinner fa-spin" /> : "Lưu"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AkkhorLayout>
  );
}
